import api from './api';
import { CREATE_ALERT_TIMEOUT_MS } from '../utils/createAlertError';

export const guardianAlertService = {

  getGuardianAlerts: async (filters = {}) => {
    try {
      const response = await api.get('/guardian_alerts/', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAlertTypes: async (filters = {}) => {
    try {
      const response = await api.get('/alert-types/', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSeverityLevels: async (filters = {}) => {
    try {
      const response = await api.get('/severity-levels/', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getCompleteAlerts: async (filters = {}) => {
    try {
      const response = await api.get('/guardian_alerts/alert_tickets_complete/', {
        params: filters,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Alertas do usuário autenticado (PROP/GUARDIAN — home de campo).
   * GET /api/guardian_alerts/get_alert_by_user/
   */
  getAlertsByUser: async () => {
    const response = await api.get('/guardian_alerts/get_alert_by_user/');
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.results)) return raw.results;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
  },

  getTicketsFromSameAlert: async (ticketId) => {
    try {
      const response = await api.get(`/guardian_alerts/get_tickets_from_same_alert/${ticketId}/`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Lista tickets de um alerta (tenta alertId e, opcionalmente, ticketId).
   * @returns {Promise<object[]>}
   */
  getTicketsForAlert: async (alertId, ticketId) => {
    const idsToTry = [alertId, ticketId].filter(Boolean);
    for (const id of idsToTry) {
      try {
        const data = await guardianAlertService.getTicketsFromSameAlert(id);
        if (Array.isArray(data?.tickets)) return data.tickets;
        if (Array.isArray(data)) return data;
      } catch {
        /* tenta próximo */
      }
    }
    return [];
  },

  /**
   * Cria ticket de resposta de campo (ciência / equipe / resposta final).
   * POST /api/alert_tickets/ (multipart)
   */
  createFieldAlertTicket: async ({
    alertId,
    action,
    responses = "nenhuma",
    observations = "",
    responsibleUserIds = [],
    attachments = [],
  }) => {
    const formData = new FormData();
    formData.append("alert", String(alertId));
    formData.append("action", action);
    formData.append("responses", responses || "nenhuma");
    formData.append("observations", observations || "");

    (responsibleUserIds || []).forEach((id) => {
      if (id != null && id !== "") {
        formData.append("responsible_user_ids", String(id));
      }
    });

    (attachments || []).forEach((item) => {
      if (!item?.file) return;
      const tipo = item.tipo || "natural";
      if (tipo === "documento") {
        formData.append("pdf_files", item.file);
      } else {
        formData.append("photos", item.file);
        formData.append("photo_types", tipo);
      }
    });

    const response = await api.post("/alert_tickets/", formData);
    return response.data;
  },

  getAlertByCode: async (codAlerta) => {
    const code = (codAlerta || "").trim().toUpperCase();
    const response = await api.get(
      `/guardian_alerts/by-code/${encodeURIComponent(code)}/`
    );
    return response.data;
  },

  /** Busca solicitação de alerta por código (e-mail FIRMS / coluna cod_alerta). */
  getAlertRequestByCode: async (codAlerta) => {
    const code = (codAlerta || "").trim().toUpperCase();
    const response = await api.get(
      `/guardian/alert_request/by-code/${encodeURIComponent(code)}/`
    );
    return response.data;
  },

  /** Uma solicitação, com focos FIRMS e distância ao KML. */
  getAlertRequest: async (requestId) => {
    const response = await api.get(`/guardian/alert_request/${requestId}/`);
    return response.data;
  },

  getAlertRequests: async (filters = {}) => {
    try {
      const response = await api.get('/guardian/list_alert_request/', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resolveAlertRequest: async (requestId, resolution) => {
    try {
      const response = await api.patch(
        `/guardian/alert_request/${requestId}/resolution/`,
        { resolution }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createAlertTicket: async (filters = {}) => {
    try {
      const response = await api.post('/alert_tickets/', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createAlertTicketMultipart: async (formData) => {
    const response = await api.post('/alert_tickets/', formData);
    return response.data;
  },

  rejectAlertResponse: async (data = {}) => {
    try {
      const response = await api.post('/alert_tickets/reject/', data); // body, não params
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createAlert: async (alertData) => {
    try {
      const response = await api.post('/alerts/', alertData, {
        timeout: CREATE_ALERT_TIMEOUT_MS,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  markAsReceived: async (alertId, userId) => {
    return api.patch(`/guardian/aprove_alert_request/`, {
      id: alertId,       // ID vai no corpo
      received_by_id: userId
    });
  },

  getAlertHistoryTable: async (area_id, params = {}) => {
    const query = new URLSearchParams({
      page_size: 1000,
      ...params,
    }).toString();
    const url = `/guardian_alerts/report_by_area/${area_id}/?${query}`;

    const response = await api.get(url);
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.results)) return raw.results;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
  },

   getAlertReportPDF: async (area_id, params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const url = query
        ? `/alerts/report/pdf/${area_id}/?${query}`
        : `/alerts/report/pdf/${area_id}/`;

      const response = await api.get(url, { responseType: "blob" });
      return response.data;
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      throw error;
    }
  },

 /*getAlertReports: async () => {
    const response = await api.get(`/alerts/reports/`);
    return response.data;
  },*/

  getAlertReports: async (area_id) => {
    const url = area_id
      ? `/alerts/reports/?area_id=${area_id}`
      : `/alerts/reports/`;
    const response = await api.get(url);
    return response.data;
  },

  downloadAlertReport: async (reportId) => {
    const response = await api.get(
      `/alert-reports/${reportId}/download/`,
      { responseType: "blob" }
    );
    return response.data;
  },

  /**
   * ADM/ENG: alertas não respondidos, estado dos envios e carga do servidor.
   * @param {"active"|"paused"|undefined} status
   * @returns {Promise<{summary: object, server_load: object, results: object[]}>}
   */
  getUnansweredAlerts: async (status) => {
    const response = await api.get('/guardian_alerts/unanswered/', {
      params: status ? { status } : {},
    });
    return response.data;
  },

  /**
   * ADM/ENG: PDF dos alertas não respondidos (por projeto/área/motivo).
   * @returns {Promise<Blob>}
   */
  getUnansweredAlertsReportPDF: async () => {
    const response = await api.get('/guardian_alerts/unanswered/report/', {
      responseType: "blob",
    });
    return response.data;
  },

  /** ADM/ENG: religa os envios de um alerta não respondido. */
  reactivateAlertNotifications: async (alertId, { resendEmail = true } = {}) => {
    const response = await api.post(
      `/guardian_alerts/${alertId}/notifications/reactivate/`,
      { resend_email: resendEmail }
    );
    return response.data;
  },

};